import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createSupplier, listSuppliers } from '../../api/masterData';
import { listPurchaseOrders } from '../../api/purchasing';
import { usePermissions } from '../../context/PermissionContext';
import {
  BusinessText,
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { PurchaseOrder, Supplier } from '../../types/domain';

/**
 * Suppliers (Master Data) — read (RLS-scoped) + create
 * (fn_create_supplier, migration 0032, permission-gated on supplier.create).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function SuppliersPage() {
  const { context, hasPermission } = usePermissions();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('supplier', 'create');
  const canReadPo = hasPermission('purchase_order', 'read');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [supplierRows, poRows] = await Promise.all([listSuppliers(), canReadPo ? listPurchaseOrders() : Promise.resolve([])]);
      setSuppliers(supplierRows);
      setPos(poRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadPo]);

  const poCountBySupplier = useMemo(() => {
    const m: Record<string, number> = {};
    for (const po of pos) m[po.supplier_id] = (m[po.supplier_id] ?? 0) + 1;
    return m;
  }, [pos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (q && !s.supplier_code.toLowerCase().includes(q) && !s.name.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
      return true;
    });
  }, [suppliers, search, statusFilter]);

  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Master data pemasok — migration 0006/0032." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Supplier baru'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewSupplierForm
          tenantId={context && context.authenticated ? context.tenant_id : ''}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Supplier', value: suppliers.length },
              { label: 'Aktif', value: suppliers.filter((s) => s.status === 'ACTIVE').length, tone: 'success' },
              { label: 'Nonaktif', value: suppliers.filter((s) => s.status === 'INACTIVE').length, tone: 'neutral' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari kode atau nama supplier..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </Select>
            }
          />
          {suppliers.length === 0 && <EmptyState text="Belum ada supplier." />}
          {suppliers.length > 0 && total === 0 && <EmptyState text="Tidak ada supplier yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Kode</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Kontak</th>
                  {canReadPo && <th style={thStyle}>PO</th>}
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => (
                  <ClickableRow key={s.supplier_id} onClick={() => setSelectedSupplier(s)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{s.supplier_code}</td>
                    <td style={tdStyle}><BusinessText text={s.name} /></td>
                    <td style={tdStyle}>{s.contact_name ?? '—'}</td>
                    {canReadPo && <td style={tdStyle}>{poCountBySupplier[s.supplier_id] ?? 0}</td>}
                    <td style={tdStyle}>
                      <StatusBadge status={s.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <SupplierDetailDrawer supplier={selectedSupplier} pos={pos} canReadPo={canReadPo} onClose={() => setSelectedSupplier(null)} />
    </div>
  );
}

function SupplierDetailDrawer({
  supplier,
  pos,
  canReadPo,
  onClose,
}: {
  supplier: Supplier | null;
  pos: PurchaseOrder[];
  canReadPo: boolean;
  onClose: () => void;
}) {
  const supplierPos = supplier ? pos.filter((po) => po.supplier_id === supplier.supplier_id) : [];

  return (
    <Drawer open={!!supplier} onClose={onClose} title={supplier ? `${supplier.supplier_code} — ${supplier.name}` : 'Detail Supplier'}>
      {supplier && (
        <div>
          <DrawerField label="Kode" value={supplier.supplier_code} />
          <DrawerField label="Nama" value={<BusinessText text={supplier.name} />} />
          <DrawerField label="Kontak" value={supplier.contact_name ?? '—'} />
          <DrawerField label="Status" value={<StatusBadge status={supplier.status} />} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Purchase Orders dari supplier ini</div>
          {!canReadPo && <p style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-faint)' }}>Anda tidak memiliki izin baca Purchase Orders.</p>}
          {canReadPo && supplierPos.length === 0 && <EmptyState text="Belum ada PO dari supplier ini." variant="compact" />}
          {canReadPo && supplierPos.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. PO</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {supplierPos.map((po) => (
                  <tr key={po.po_id}>
                    <td style={tdStyle}>{po.po_number}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={po.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}

function NewSupplierForm({ tenantId, onCreated }: { tenantId: string; onCreated: () => void }) {
  const [supplierCode, setSupplierCode] = useState('');
  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createSupplier({ tenantId, supplierCode, name, contactName: contactName || null });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 20, padding: 16 }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Kode">
          <TextInput value={supplierCode} onChange={(e) => setSupplierCode(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 220 }}>
        <FieldLabel label="Nama">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 200 }}>
        <FieldLabel label="Kontak">
          <TextInput value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
